import { useEffect, useState } from "react";
import { getRuntimeJobs } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Job {
  id: string;
  agent_name: string;
  status: string;
  model: string | null;
  tokens_used: number | null;
  duration_ms: number | null;
  created_at: string;
  error_message: string | null;
  retry_count: number;
}

const STATUS_COLOR: Record<string, string> = {
  RUNNING: "text-blue-400 bg-blue-900/30",
  COMPLETED: "text-green-400 bg-green-900/30",
  FAILED: "text-red-400 bg-red-900/30",
  QUEUED: "text-yellow-400 bg-yellow-900/30",
};

export function OpsJobsPage() {
  const { token } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void getRuntimeJobs(token, { status: statusFilter || undefined, limit: 50 }).then(
      (d: unknown) => {
        setJobs((d as { items: Job[] }).items ?? []);
        setLoading(false);
      },
    );
  }, [statusFilter]);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Running Jobs</h1>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm text-gray-300"
          >
            <option value="">All statuses</option>
            {["QUEUED", "RUNNING", "COMPLETED", "FAILED"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-2">
            {jobs.length === 0 && <p className="text-gray-400">No jobs found.</p>}
            {jobs.map((job) => (
              <div
                key={job.id}
                className="flex items-center gap-4 rounded-lg border border-gray-800 bg-gray-900 p-3"
              >
                <span
                  className={`shrink-0 rounded px-2 py-0.5 text-xs ${STATUS_COLOR[job.status] ?? "bg-gray-800 text-gray-400"}`}
                >
                  {job.status}
                </span>
                <span className="text-sm font-medium text-white">{job.agent_name}</span>
                {job.model && <span className="text-xs text-gray-500">{job.model}</span>}
                {job.tokens_used != null && (
                  <span className="text-xs text-gray-500">
                    {job.tokens_used.toLocaleString()} tokens
                  </span>
                )}
                {job.duration_ms != null && (
                  <span className="text-xs text-gray-500">{job.duration_ms}ms</span>
                )}
                {job.retry_count > 0 && (
                  <span className="text-xs text-yellow-500">retry #{job.retry_count}</span>
                )}
                <span className="ml-auto text-xs text-gray-600">
                  {new Date(job.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
